import React, { useEffect, useRef, useState } from "react";
import {
  BRANDING_GALLERY_MAX,
} from "../../config/clubWebsitePages";
import { getCloudinaryMediaBaseUrl } from "../../config/cloudinaryBase";
import { fileToBase64, normalizeBrandingGalleryForState } from "../../helpers/brandingGallery";
import { resolveCloudinaryImageSrc } from "../../helpers/helper.service";
import "./brandingGalleryField.css";

function galleryPreviewSrc(url, cloudinaryBase) {
  const s = String(url || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || /^blob:/i.test(s)) {
    return s;
  }
  return resolveCloudinaryImageSrc(s, cloudinaryBase, "");
}

/** Where each index would land while dragging (without mutating the list). */
function getShiftedIndex(index, dragFrom, hoverTo) {
  if (dragFrom == null || hoverTo == null || dragFrom === hoverTo) return index;
  if (index === dragFrom) return hoverTo;
  if (dragFrom < hoverTo) {
    if (index > dragFrom && index <= hoverTo) return index - 1;
  } else if (index >= hoverTo && index < dragFrom) {
    return index + 1;
  }
  return index;
}

function useStableGalleryIds(list) {
  const idMapRef = useRef(new Map());
  const seqRef = useRef(0);

  useEffect(() => {
    const urls = new Set(list);
    idMapRef.current.forEach((_, url) => {
      if (!urls.has(url)) idMapRef.current.delete(url);
    });
  }, [list]);

  const getId = (url) => {
    if (!idMapRef.current.has(url)) {
      idMapRef.current.set(url, `gallery-${seqRef.current++}`);
    }
    return idMapRef.current.get(url);
  };

  return getId;
}

/**
 * Sortable branding gallery (max 10). New files → base64 data URLs in state;
 * existing images stay as https URLs from GET /club/admin/:id.
 */
const BrandingGalleryField = ({
  t,
  images,
  onChange,
  disabled = false,
  maxImages = BRANDING_GALLERY_MAX,
  onError,
  showPreview = true,
}) => {
  const fileRef = useRef(null);
  const gridRef = useRef(null);
  const itemRefs = useRef({});
  const layoutRef = useRef({ cols: 1, cellW: 0, cellH: 0, gap: 12 });
  const slotRectsRef = useRef([]);
  const dragRef = useRef(null);
  const rafRef = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  const [adding, setAdding] = useState(false);
  const cloudinaryBase = getCloudinaryMediaBaseUrl();
  const list = normalizeBrandingGalleryForState(images);
  const getStableId = useStableGalleryIds(list);
  const isDragging = dragIndex != null;

  const notifyError = (key) => {
    if (onError) onError(t(key));
  };

  const measureLayout = () => {
    const grid = gridRef.current;
    if (!grid || list.length === 0) return;

    const style = window.getComputedStyle(grid);
    const gap = parseFloat(style.gap || style.rowGap || "12") || 12;
    const first = itemRefs.current[getStableId(list[0])];
    if (!first) return;

    const cellW = first.offsetWidth;
    const cellH = first.offsetHeight;
    let cols = 1;

    const firstTop = first.offsetTop;
    for (let i = 1; i < list.length; i += 1) {
      const el = itemRefs.current[getStableId(list[i])];
      if (!el) break;
      if (Math.abs(el.offsetTop - firstTop) < 4) cols += 1;
      else break;
    }

    layoutRef.current = { cols, cellW, cellH, gap };
  };

  const captureSlotRects = () =>
    list.map((url) => {
      const el = itemRefs.current[getStableId(url)];
      return el ? el.getBoundingClientRect() : null;
    });

  const indexFromPoint = (x, y, rects) => {
    if (!rects.length) return 0;
    for (let i = 0; i < rects.length; i += 1) {
      const r = rects[i];
      if (r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
        return i;
      }
    }
    let best = 0;
    let bestDist = Infinity;
    rects.forEach((r, i) => {
      if (!r) return;
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const d = (cx - x) ** 2 + (cy - y) ** 2;
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    return best;
  };

  const getItemTransform = (index) => {
    if (!isDragging || dragIndex == null || hoverIndex == null) {
      return "translate(0, 0)";
    }
    if (index === dragIndex) return "translate(0, 0)";

    const shifted = getShiftedIndex(index, dragIndex, hoverIndex);
    if (shifted === index) return "translate(0, 0)";

    const { cols, cellW, cellH, gap } = layoutRef.current;
    const fromRow = Math.floor(index / cols);
    const fromCol = index % cols;
    const toRow = Math.floor(shifted / cols);
    const toCol = shifted % cols;
    const dx = (toCol - fromCol) * (cellW + gap);
    const dy = (toRow - fromRow) * (cellH + gap);
    return `translate(${dx}px, ${dy}px)`;
  };

  const finishDrag = (commit) => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const active = dragRef.current;
    dragRef.current = null;

    if (commit && active) {
      const { from, to } = active;
      if (from != null && to != null && from !== to) {
        const next = [...list];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        onChange(next);
      }
    }

    setDragIndex(null);
    setHoverIndex(null);
    setDragPos(null);
  };

  const onPointerMove = (e) => {
    const active = dragRef.current;
    if (!active) return;

    const x = e.clientX;
    const y = e.clientY;

    if (
      !active.started &&
      Math.hypot(x - active.startX, y - active.startY) < 6
    ) {
      return;
    }

    if (!active.started) {
      active.started = true;
      measureLayout();
      slotRectsRef.current = captureSlotRects();
      setDragIndex(active.from);
      setHoverIndex(active.from);
    }

    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const rects = slotRectsRef.current;
      const nextHover = indexFromPoint(x, y, rects);
      setHoverIndex((prev) => (prev === nextHover ? prev : nextHover));
      setDragPos({
        x: x - active.offsetX,
        y: y - active.offsetY,
        w: active.width,
        h: active.height,
      });
    });
  };

  const onPointerUp = () => {
    const active = dragRef.current;
    if (!active) return;
    finishDrag(active.started);
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerUp);
  };

  const onItemPointerDown = (e, index) => {
    if (disabled || e.button !== 0) return;
    if (e.target.closest(".branding-gallery-remove")) return;

    const el = itemRefs.current[getStableId(list[index])];
    if (!el) return;

    const rect = el.getBoundingClientRect();
    dragRef.current = {
      from: index,
      to: index,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      started: false,
    };

    e.currentTarget.setPointerCapture?.(e.pointerId);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
  };

  useEffect(() => {
    if (!isDragging) measureLayout();
  }, [list.length, isDragging]);

  useEffect(
    () => () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  useEffect(() => {
    if (dragIndex == null || hoverIndex == null) return;
    if (dragRef.current) dragRef.current.to = hoverIndex;
  }, [dragIndex, hoverIndex]);

  const onAddFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || disabled) return;

    const imageFiles = files.filter((file) => file.type?.startsWith("image/"));
    if (imageFiles.length !== files.length) {
      notifyError("CLUB_LOGO_INVALID_TYPE");
    }
    if (!imageFiles.length) return;

    const remaining = maxImages - list.length;
    if (remaining <= 0) return;

    const toAdd = imageFiles.slice(0, remaining);
    if (imageFiles.length > remaining) {
      notifyError("CLUB_BRAND_GALLERY_TOO_MANY");
    }

    setAdding(true);
    try {
      const base64s = await Promise.all(toAdd.map((file) => fileToBase64(file)));
      onChange([...list, ...base64s]);
    } catch {
      notifyError("CLUB_BRAND_GALLERY_ADD_FAILED");
    } finally {
      setAdding(false);
    }
  };

  const removeAt = (idx) => {
    if (disabled) return;
    onChange(list.filter((_, i) => i !== idx));
  };

  const draggedUrl = dragIndex != null ? list[dragIndex] : null;

  return (
    <div className="branding-gallery-field">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <p className="form-label mb-0">{t("CLUB_BRAND_GALLERY_TITLE")}</p>
        <span className="text-muted small branding-gallery-counter">
          {list.length} / {maxImages} {t("CLUB_BRAND_GALLERY_IMAGES")}
        </span>
      </div>
      <p className="text-muted small mb-3">{t("CLUB_BRAND_GALLERY_HINT")}</p>

      {list.length > 0 ? (
        <div
          ref={gridRef}
          className={[
            "branding-gallery-grid",
            isDragging ? "branding-gallery-grid--dragging" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          role="list"
          aria-label={t("CLUB_BRAND_GALLERY_TITLE")}
        >
          {list.map((url, idx) => {
            const stableId = getStableId(url);
            const isActive = dragIndex === idx;
            const displayOrder =
              isDragging && hoverIndex != null
                ? getShiftedIndex(idx, dragIndex, hoverIndex) + 1
                : idx + 1;

            return (
              <div
                key={stableId}
                ref={(el) => {
                  if (el) itemRefs.current[stableId] = el;
                  else delete itemRefs.current[stableId];
                }}
                className={[
                  "branding-gallery-item",
                  isActive ? "branding-gallery-item--source" : "",
                  isDragging && !isActive ? "branding-gallery-item--animating" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={
                  isDragging && !isActive
                    ? { transform: getItemTransform(idx) }
                    : undefined
                }
                role="listitem"
                onPointerDown={(e) => onItemPointerDown(e, idx)}
              >
                <img
                  src={galleryPreviewSrc(url, cloudinaryBase)}
                  alt=""
                  className="branding-gallery-thumb"
                  draggable={false}
                  onError={(ev) => {
                    ev.target.style.display = "none";
                  }}
                />
                <span className="branding-gallery-order" aria-hidden>
                  {displayOrder}
                </span>
                {!disabled ? (
                  <>
                    <span className="branding-gallery-drag-hint" aria-hidden>
                      ⋮⋮
                    </span>
                    <button
                      type="button"
                      className="branding-gallery-remove"
                      aria-label={t("CLUB_BRAND_GALLERY_REMOVE")}
                      disabled={disabled}
                      onClick={() => removeAt(idx)}
                    >
                      ×
                    </button>
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-muted small mb-0 branding-gallery-empty">
          {t("CLUB_BRAND_GALLERY_EMPTY")}
        </p>
      )}

      {isDragging && dragPos && draggedUrl ? (
        <div
          className="branding-gallery-float"
          style={{
            width: dragPos.w,
            height: dragPos.h,
            transform: `translate(${dragPos.x}px, ${dragPos.y}px)`,
          }}
          aria-hidden
        >
          <img
            src={galleryPreviewSrc(draggedUrl, cloudinaryBase)}
            alt=""
            className="branding-gallery-thumb"
            draggable={false}
          />
          <span className="branding-gallery-order">
            {(hoverIndex ?? dragIndex) + 1}
          </span>
        </div>
      ) : null}

      {!disabled ? (
        <div className="mt-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            className="d-none"
            disabled={disabled || adding || list.length >= maxImages}
            onChange={(e) => void onAddFiles(e)}
          />
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={disabled || adding || list.length >= maxImages}
            onClick={() => fileRef.current?.click()}
          >
            {adding
              ? t("CLUB_BRAND_GALLERY_ADDING")
              : t("CLUB_BRAND_GALLERY_ADD")}
          </button>
          {list.length >= maxImages ? (
            <p className="text-muted small mt-2 mb-0">
              {t("CLUB_BRAND_GALLERY_MAX_REACHED")}
            </p>
          ) : null}
        </div>
      ) : null}

      {showPreview && list.length > 0 ? (
        <div className="branding-gallery-preview mt-3">
          <p className="small text-muted mb-2">{t("CLUB_BRAND_GALLERY_PREVIEW")}</p>
          <div className="branding-gallery-preview-track">
            {list.map((url) => (
              <img
                key={`preview-${getStableId(url)}`}
                src={galleryPreviewSrc(url, cloudinaryBase)}
                alt=""
                className="branding-gallery-preview-img"
                draggable={false}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BrandingGalleryField;
